import React, { useEffect, useState } from 'react';
import { Avatar, Box, CircularProgress, Chip, Collapse, Divider, Typography, Tooltip } from '@mui/material';

import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import Segmentacion from '../components/SearchPageComponents/SearchResults/Segmentacion';
import '../components/cardViews/cardViews.css';
import messengerIcon from '../assets/messenger.png';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import RequestSvc from "../services/RequestSvc";
import CardViewAdDetail from "../components/cardViews/CardViewAdDetail";
import { FaGlobeAfrica } from 'react-icons/fa';
import { IconContext } from "react-icons";
import FunnelsModal from '../components/modalwindow/FunnelsModal';

// Base URL for ads API and helper to fetch total ads by pageId
const ADS_API_BASE = 'https://pulpodevia.com/ads';
const ADS_TOTALS_TIMEOUT_MS = 30000;

function createTimedRequestSignal(parentSignal, timeoutMs) {
    const controller = new AbortController();
    let didTimeout = false;

    const abortFromParent = () => {
        controller.abort(parentSignal?.reason);
    };

    if (parentSignal?.aborted) {
        abortFromParent();
    } else if (parentSignal) {
        parentSignal.addEventListener('abort', abortFromParent, { once: true });
    }

    const timeoutId = setTimeout(() => {
        didTimeout = true;
        controller.abort(new DOMException('Request timed out', 'TimeoutError'));
    }, timeoutMs);

    return {
        signal: controller.signal,
        didTimeout: () => didTimeout,
        cleanup: () => {
            clearTimeout(timeoutId);
            parentSignal?.removeEventListener('abort', abortFromParent);
        }
    };
}

async function requestTotalAds(pageId, signal, proxyMode) {
    const timedRequest = createTimedRequestSignal(signal, ADS_TOTALS_TIMEOUT_MS);

    try {
        const res = await fetch(`${ADS_API_BASE}?gettotalads=${encodeURIComponent(pageId)}&proxy=on`, {
        //const res = await fetch(`http://192.168.1.84:8080/ads?gettotalads=${encodeURIComponent(pageId)}&proxy=on`, {
            signal: timedRequest.signal,
            cache: 'no-store'
        });

        if (!res.ok) {
            return { ok: false, pid: pageId, error: `HTTP ${res.status}` };
        }

        const data = await res.json().catch(() => ({}));
        const total = Number(data?.totalads ?? data?.totalAds ?? 0);
        const n = Number.isFinite(total) ? total : 0;
        return { ok: true, pid: pageId, n };
    } catch (err) {
        if (signal?.aborted) {
            throw err;
        }

        if (timedRequest.didTimeout()) {
            return { ok: false, pid: pageId, error: 'timeout', timedOut: true };
        }

        return { ok: false, pid: pageId, error: err?.message || 'network-error' };
    } finally {
        timedRequest.cleanup();
    }
}

async function fetchTotalAds(pageId, signal) {
    try {
        const directResult = await requestTotalAds(pageId, signal, 'off');
        if (directResult?.ok || !directResult?.timedOut) {
            return directResult;
        }

        return await requestTotalAds(pageId, signal, 'on');
    } catch (err) {
        if (signal?.aborted) {
            return { ok: false, pid: pageId, error: 'aborted' };
        }

        return { ok: false, pid: pageId, error: err?.message || 'network-error' };
    }
}

// Fetch historical totals (past week and past 15 days)
async function fetchHistoricalTotals(pageId, signal) {
    try {
        const res = await fetch(`${ADS_API_BASE}?reversedaysads=${encodeURIComponent(pageId)}`, { signal, cache: 'no-store' });
        if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
        const data = await res.json().catch(() => ({}));
        const rawWeek = data?.total_ads_past_week;
        const raw15 = data?.total_ads_15_days;
        const pastWeek = Number.isFinite(Number(rawWeek)) ? Number(rawWeek) : null;
        const past15 = Number.isFinite(Number(raw15)) ? Number(raw15) : null;
        return { ok: true, pastWeek, past15 };
    } catch (e) {
        return { ok: false, error: e?.message || 'network-error' };
    }
}

// Small animated dots indicator for pending state
function DotDots({ interval = 400 }) {
    const [dots, setDots] = useState('');
    useEffect(() => {
        const id = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), interval);
        return () => clearInterval(id);
    }, [interval]);
    // Reserve width to avoid layout shift (3 chars)
    return <span style={{ display: 'inline-block', width: 18 }} aria-live="polite">{dots}</span>;
}

// Blinking red dot indicator when fire badge AND high total
function BlinkingRedDot({ interval = 1000 }) {
    const [on, setOn] = useState(true);
    useEffect(() => {
        const id = setInterval(() => setOn(o => !o), interval);
        return () => clearInterval(id);
    }, [interval]);
    return <span aria-label="High activity" title="Alta actividad" style={{ display: 'inline-block', width: '1em', textAlign: 'center' }}>{on ? '🔴' : ' '}</span>;
}


/**
 * A simple, defensive component to display media for the error case.
 * It checks if the URL is for a video or an image.
 */
const AdMediaDisplay = ({ mediaUrl }) => {
    // Return nothing if the URL is invalid or missing
    if (!mediaUrl || typeof mediaUrl !== 'string') {
        return (
            <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#f0f0f0', borderRadius: '8px', m: 1 }}>
                <Typography variant="body2" color="textSecondary">
                    Media no disponible
                </Typography>
            </Box>
        );
    }

    const isVideo = ['.mp4', '.mov', '.webm'].some(ext => mediaUrl.toLowerCase().includes(ext));

    if (isVideo) {
        return (
            <video 
                src={mediaUrl} 
                controls 
                muted
                autoPlay
                loop
                style={{ width: '100%', display: 'block', borderRadius: '8px' }} 
            />
        );
    } else {
        return (
            <img 
                src={mediaUrl} 
                alt="Ad creative" 
                style={{ width: '100%', display: 'block', borderRadius: '8px' }} 
            />
        );
    }
};

/**
 * REFACCTORIZADO: Se ha mejorado el manejo de errores para que la UI
 * maneje una respuesta 200 OK que contenga un payload de error.
 */
export default function DetailsView({ item, launchCategoryDialog, isVault = false }) {

    const [selectedPost, setSelectedPost] = useState(null);
    const [showFullDescription1, setShowFullDescription1] = useState(false);
    const [expandTitle, setExpandTitle] = useState(false);
    const [copiedDescripcion, setCopiedDescripcion] = useState(false);
    const [copiedTitle, setCopiedTitle] = useState(false);
    const [segmentacion, setSegmentacion] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalAds, setTotalAds] = useState(null);
    const [isAdsPending, setIsAdsPending] = useState(false);
    const [totalAdsError, setTotalAdsError] = useState(false);
    const [adsBadge, setAdsBadge] = useState(null); // 'up' | 'fire' | null
    const [pastWeekAds, setPastWeekAds] = useState(null);
    const [past15DaysAds, setPast15DaysAds] = useState(null);
    const [funnelsModalState, setFunnelsModalState] = useState({ open: false, libraryId: null });

    const fetchAdsData = async (newItem) => {
        try {
            let svc = new RequestSvc();
            if (newItem.link_url) {
                let url = `https://test.tueducaciondigital.site/getfbads?pageID=${newItem.pageID}`;
                let result = await svc.get(url);
                if (result?.error) {
                    console.log("Failed to fetch secondary user data");
                } else {
                    setSegmentacion(result);
                }
            }
        } catch (err) {
            console.log("Error loading totals for segmentacion:", err);
        }
    };


    // --- API CALL REFACTOR ---
    useEffect(() => {
        const fetchAdDetails = async () => {
            setLoading(true);
            setError(null);
            let response;
            try {
                if(isVault) {
                    // obtiene datos de los anuncios  guardados por el usuario
                     response = await fetch(`https://pulpoia-ops.com/backend/ads/getadssaveddetailsforcardview?ids=${item.LibraryID}`);
                     //response = await fetch(`http://localhost:3002/ads/getadssaveddetailsforcardview?ids=${item.LibraryID}`);

                } else {
                    // obtiene datos de los anuncios no guardados por el usuario
                     response = await fetch(`https://pulpoia-ops.com/backend/ads/getadsdetailsforcardview?ids=${item.LibraryID}`);
                     //response = await fetch(`http://localhost:3002/ads/getadsdetailsforcardview?ids=${item.LibraryID}`);
                }

                //response = await fetch(`https://tueducaciondigital.site/ads/getadsdetailsforcardview?ids=${item.LibraryID}`);                     
                
                
                const result = await response.json();

                if (result.error || result.data.length === 0) {
                    console.error("API returned an error payload:", result.error);
                    setError({ type: 'API_ERROR', message: "META YA NO TIENE INFORMACION DE ESTE ANUNCIO" });
                } else {
                    setShowFullDescription1(false);
                    let details = {
                        ...item,
                        ...result.data[0],
                        embudo_landingPage: item?.embudo_landingPage ?? result.data[0]?.embudo_landingPage ?? null
                    };
                    if (details.AdCreative === null || details.AdCreative === "") {
                        details.AdCreative = item.AdCreative;
                        details.AdMedia = item.AdMedia;
                    }
                    setSelectedPost(details);
                    fetchAdsData(details);
                }

            } catch (err) {
                console.error("Fatal error fetching ad details:", err);
                setError({ type: 'GENERIC_ERROR', message: "Ocurrió un error de red al cargar los detalles." });
            } finally {
                setLoading(false);
            }
        };

        if (item && item.LibraryID) {
            fetchAdDetails();
        } else {
            setLoading(false);
            setError({ type: 'MISSING_ID', message: 'No se proporcionó un ID de anuncio válido.' });
        }
    }, [item]);


    // Fetch current and historical totals when pageID available
    useEffect(() => {
        const firstPid = selectedPost?.pageID || item?.pageID;
        if (!firstPid) return;

        setIsAdsPending(true);
        setTotalAdsError(false);
        setAdsBadge(null);
        const abort = new AbortController();
        let cancelled = false;

        // Defer to allow pending UI paint
        const timer = setTimeout(async () => {
            if (cancelled) return;
            const [probe, hist] = await Promise.all([
                fetchTotalAds(firstPid, abort.signal),
                fetchHistoricalTotals(firstPid, abort.signal)
            ]);
            if (abort.signal.aborted || cancelled) return;

            if (probe?.ok) {
                setTotalAds(probe.n);
            } else {
                setTotalAdsError(true);
            }

            if (hist?.ok) {
                setPastWeekAds(hist.pastWeek);
                setPast15DaysAds(hist.past15);
            } else {
                setPastWeekAds(null);
                setPast15DaysAds(null);
            }
            

            // Badge logic per spec
            if (probe?.ok) {
                const current = probe.n;
                const week = hist?.ok ? hist.pastWeek : null;
                const days15 = hist?.ok ? hist.past15 : null;
                console.log('OJO Badge Logic:', { current, week, days15 });

                if (Number.isFinite(current) && Number.isFinite(week) && Number.isFinite(days15) && current > week && week > days15) {
                    setAdsBadge('fire');
                } else if (Number.isFinite(current) && Number.isFinite(week) && (days15 == null) && current > week) {
                    setAdsBadge('up');
                } else if (week == null && days15 == null) {
                    setAdsBadge(null);
                } else {
                    setAdsBadge(null);
                }
            }

            setIsAdsPending(false);
        }, 0);

        return () => { cancelled = true; abort.abort(); clearTimeout(timer); };
    }, [selectedPost?.pageID, item?.pageID]);


    const handleCopyToClipboard = (textToClipboard, nameContainer) => {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = textToClipboard;
        const textToCopy = tempElement.innerText || tempElement.textContent;

        navigator.clipboard.writeText(textToCopy).then(() => {
            if (nameContainer === "Descripcion") {
                setCopiedDescripcion(true);
                setTimeout(() => setCopiedDescripcion(false), 1500);
            } else {
                setCopiedTitle(true);
                setTimeout(() => setCopiedTitle(false), 1500);
            }
        }).catch((err) => console.error("Error al copiar al portapapeles:", err));
    };

    const getDomainFromUrl = (url) => {
        try {
            const parsedUrl = new URL(url);
            return parsedUrl.hostname;
        } catch (error) {
            return null;
        }
    };

    const hasEmbudoLandingPage = ((typeof selectedPost?.embudo_landingPage === 'string' && selectedPost.embudo_landingPage.trim() !== '') ||
        (typeof selectedPost?.embudo_landingPage !== 'string' && selectedPost?.embudo_landingPage != null));

    const openFunnelsModal = () => {
        setFunnelsModalState({
            open: true,
            libraryId: item?.LibraryID ?? selectedPost?.LibraryID ?? null,
        });
    };

    const closeFunnelsModal = () => {
        setFunnelsModalState((prev) => ({ ...prev, open: false }));
    };


    // --- FINAL Error Display Logic ---
    return (
        <div className={"card-view-ad-layout-main-detail"}>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <CircularProgress />
                </Box>
            ) : error ? (
                // Use the new, robust error display
                <Box className="card-container-details">
                    <AdMediaDisplay mediaUrl={item?.AdMedia} />
                    <Box sx={{ p: 2, textAlign: 'center', mt: 1 }}>
                        <Typography variant="h6" color="error">
                            {error.message}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                selectedPost && (
                    <Box className="card-container-details">
                        <Box sx={{ display: 'flex', pt: 1, pb: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                {selectedPost?.pageID && (
                                    <a
                                        href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=ALL&media_type=all&search_type=page&view_all_page_id=${selectedPost.pageID}`}
                                        target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        <Avatar src={selectedPost.page_profile_picture_url || ''} alt={selectedPost.pageName || 'Page Avatar'} sx={{ ml: 1 }} />
                                    </a> 
                                )}
                                <Box>
                                    <Typography fontSize="14px" fontWeight="bold">
                                        {selectedPost?.pageID ? (
                                            <a href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=ALL&media_type=all&search_type=page&view_all_page_id=${selectedPost.pageID}`}
                                                target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}
                                            >
                                                {selectedPost.pageName || 'Untitled Page'}
                                            </a>
                                        ) : 'Untitled Page'}
                                    </Typography>
                                    <Typography fontSize="12px" color="#fff">
                                        { 'Total anuncios: ' }
                                        {isAdsPending ? (
                                            <>
                                                {'…'}
                                                <DotDots />
                                            </>
                                        ) : Number.isFinite(totalAds) ? (
                                            <>
                                                {adsBadge === 'fire' ? '🔥 ' : adsBadge === 'up' ? '⬆️ ' : ''}
                                                {adsBadge === 'fire' && totalAds > 50 && <BlinkingRedDot /> }
                                                {' '}
                                                {totalAds}
                                            </>
                                        ) : totalAdsError ? (
                                            <>
                                                {'—'}
                                                <span title="No disponible"> ⚠️</span>
                                            </>
                                        ) : null}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <CardViewAdDetail item={selectedPost} launchCategoryDialog={launchCategoryDialog} isVault={isVault} />

                        <Box display="grid" sx={{ m: 1 }}>
                            <Box display={'flex'} sx={{ alignItems: 'center', mt: 1 }}>
                                <Typography fontSize="14px" fontWeight="bold">Texto principal</Typography>
                                {selectedPost?.__html && (
                                    <Tooltip title={copiedDescripcion ? "Copiado!" : "Copiar"} open={copiedDescripcion} placement="top" arrow>
                                        <ContentCopyOutlinedIcon sx={{ ml: 1, cursor: 'pointer' }} onClick={() => handleCopyToClipboard(selectedPost.__html, "Descripcion")} />
                                    </Tooltip>
                                )}
                            </Box>
                            <Collapse collapsedSize={24} in={showFullDescription1}>
                                <Typography
                                    fontSize="12px"
                                    className={showFullDescription1 ? 'card-details-descriptions' : 'card-details-descriptions close'}
                                    mt={1}
                                    dangerouslySetInnerHTML={{ __html: (selectedPost?.__html && !selectedPost.__html.includes("product.")) ? selectedPost.__html : "" }}
                                />
                            </Collapse>
                            {selectedPost?.__html && selectedPost.__html.length > 80 && (
                                <Typography
                                    onClick={() => setShowFullDescription1(!showFullDescription1)}
                                    sx={{ width: '110px', color: 'black', mt: 1, fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {showFullDescription1 ? 'Mostrar menos' : '...más'}
                                </Typography>
                            )}
                        </Box>

                        {selectedPost?.title && selectedPost.title !== '' && (
                            <Box display="grid" alignItems="center" sx={{ m: 1 }}>
                                <Box display={'flex'} sx={{ alignItems: 'center', mt: 1 }}>
                                    <Typography fontSize="14px" fontWeight="bold">Título</Typography>
                                    <Tooltip title={copiedTitle ? "Copiado!" : "Copiar"} open={copiedTitle} placement="top" arrow>
                                        <ContentCopyOutlinedIcon sx={{ ml: 1, cursor: 'pointer' }} onClick={() => handleCopyToClipboard(selectedPost.title, "Title")} />
                                    </Tooltip>
                                </Box>
                                <Collapse collapsedSize={28} in={expandTitle}>
                                    <Typography fontSize="12px" sx={{ mt: 1 }}>
                                        {!selectedPost.title?.includes("product.") ? selectedPost.title : ""}
                                    </Typography>
                                </Collapse>
                                {selectedPost.title.length > 70 && (
                                    <Chip
                                        size={'medium'}
                                        label={expandTitle ? 'Mostrar menos' : 'Ver más'}
                                        onClick={() => setExpandTitle(!expandTitle)}
                                        sx={{ width: '110px', color: 'black', mt: 1, fontWeight: 'bold' }}
                                    />
                                )}
                            </Box>
                        )}

                        <Divider />

                        <Box display="flex" alignItems="center" gap={1} sx={{ m: 1 }}>
                            <Typography fontSize="14px" fontWeight="bold">Plataformas:</Typography>
                            {selectedPost.publisherPlatform?.includes('instagram') && <InstagramIcon />}
                            {selectedPost.publisherPlatform?.includes('facebook') && <FacebookRoundedIcon />}
                            {selectedPost.publisherPlatform?.includes('messenger') && (
                                <img src={messengerIcon} alt={'messenger icon'} style={{ width: '20px', height: '20px' }} />
                            )}
                        </Box>

                        {selectedPost?.LibraryID && (
                            <Box display="flex" alignItems="center" gap={1} sx={{ m: 1 }}>
                                <Typography fontSize="14px" fontWeight="bold">Identificador del anuncio:</Typography>
                                <a
                                    id="watchAd"
                                    href={`https://www.facebook.com/ads/library/?active_status=ALL&ad_type=all&country=ALL&q=${selectedPost.LibraryID}`}
                                    target="_blank" rel="noopener noreferrer"
                                >
                                    {selectedPost.LibraryID}
                                </a>
                            </Box>
                        )}

                        {selectedPost?.link_url && (
                            <Box sx={{ m: 1 }}>
                                <Box display="flex" alignItems="center" gap={2} sx={{ p: 1, border: 1, borderRadius: '4px', borderColor: 'grey.300' }}>
                                    <a href={selectedPost.link_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                        <OpenInNewIcon />
                                    </a>
                                    <Typography width="100%" maxWidth="200px" overflow="hidden" whiteSpace={"nowrap"} textOverflow="ellipsis">
                                        {selectedPost.link_url}
                                    </Typography>
                                    {selectedPost.cta_text && (
                                        <a href={selectedPost.link_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                            <Chip label={selectedPost.cta_text} sx={{ border: 1, borderRadius: '4px', borderColor: 'grey.300', background: 'transparent', position: 'absolute', right: 1, mr: '56px', mt: '-15px' }} />
                                        </a>
                                    )}
                                    <IconContext.Provider value={{ size: "24px", style: { position: 'absolute', right: 1, marginRight: '16px' } }}>
                                        {getDomainFromUrl(selectedPost.link_url) && (
                                            <Tooltip title="Buscar en la biblioteca todos los anuncios que contengan este Dominio">
                                                <a id="watchAd" href={`https://www.facebook.com/ads/library/?active_status=ALL&ad_type=all&country=ALL&q="${getDomainFromUrl(selectedPost.link_url)}"`}
                                                   target="_blank" rel="noopener noreferrer"
                                                >
                                                    <FaGlobeAfrica style={{ marginTop: '-10px' }} />
                                                </a>
                                            </Tooltip>
                                        )}
                                    </IconContext.Provider>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                                                        {hasEmbudoLandingPage && (
                                        <Chip
                                            label="Ver Embudos"
                                                                                        clickable
                                                                                onClick={openFunnelsModal}
                                            sx={{
                                                color: '#ffeb00',
                                                border: '1px solid #6b6b6b',
                                                borderRadius: '4px',
                                                backgroundColor: 'transparent',
                                                fontSize: '14px'
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}

                        {segmentacion && (
                            <div style={{ marginTop: '20px' }}>
                                <Segmentacion selectedPost={segmentacion} />
                            </div>
                        )}

                        <FunnelsModal
                            open={funnelsModalState.open}
                            onClose={closeFunnelsModal}
                            queryId={funnelsModalState.libraryId}
                        />
                    </Box>
                )
            )}
        </div>
    );
}
